import { getPlanInfo } from '@mm/state/helpers/billing-info-helpers';
import { SubscriptionTabDefinition } from '@mm/state/helpers/billing-subscription-tab-helpers';
import { EnhancedBillingPage } from '@mm/state/interfaces/connected-page';
import { isBilling2023Subscription } from '@mm/state/model/SubscriptionModel';
import React, { useMemo } from 'react';
import { getBillingPlanForTab } from '../../helpers';
import { SubscriptionMetaField } from '../../SubscriptionMetaField/SubscriptionMetaField';

import '../BillingPageCard.pcss';
import { useBillingDetails } from '../use-billing-details';

type Props = {
  page: EnhancedBillingPage;
  planTab: SubscriptionTabDefinition;
};

// tslint:disable-next-line: cyclomatic-complexity
export const BillingPageMetadata = (props: Props) => {
  const { page, planTab } = props;

  const subscription = page?.subscription || planTab?.teamSubscription;
  const billingPlan = getBillingPlanForTab(planTab);

  const planInfoMemoised = useMemo(
    () => getPlanInfo(page, billingPlan),
    [page, billingPlan],
  );

  const {
    currentSubscription,
    subscriptionTerm,
    subscriptionStatus,
    subscriptionEndsAt,
    nextBillCombinedInfo,
    nextBillingDate,
    thisPeriodSeats,
    nextPeriodSeats,
    billedWith,
    billingPeriod,
    thisPeriodCredits,
    nextPeriodTotalCredits,
  } = useBillingDetails(page, planInfoMemoised, planTab.teamSubscription);

  const showSubscr =
    subscription &&
    currentSubscription?.nextInvoiceAmount &&
    currentSubscription?.nextInvoiceAmount.sum;

  const showFieldNextBillAmount = Boolean(
    showSubscr && subscription && currentSubscription,
  );

  const isBilling2023Plan =
    currentSubscription && isBilling2023Subscription(currentSubscription);

  const showSendCreditsAllotmentsAndSeats = !(
    planInfoMemoised.isLegacyTeamPlan ||
    planInfoMemoised.isCustomTeam ||
    planInfoMemoised.isSpecialTeam ||
    planInfoMemoised.isLegacyProPlan
  );

  const isTeamPlan = planInfoMemoised.isAnyTeamPlan;

  const allotedCredits = (
    <div className="billing-page-card__plan-info--period-details">
      {thisPeriodCredits && (
        <div className="billing-page-card__plan-info--period-details--left">
          This {billingPeriod}: {thisPeriodCredits}
        </div>
      )}
      {nextPeriodTotalCredits && (
        <div className="billing-page-card__plan-info--period-details--right">
          Next {billingPeriod}: {nextPeriodTotalCredits}
        </div>
      )}
    </div>
  );

  const additionalSeats = (
    <div className="billing-page-card__plan-info--period-details">
      {(thisPeriodSeats || thisPeriodSeats === 0) && (
        <div className="billing-page-card__plan-info--period-details--left">
          This {billingPeriod}: {thisPeriodSeats}
        </div>
      )}
      {(nextPeriodSeats || nextPeriodSeats === 0) && (
        <div className="billing-page-card__plan-info--period-details--right">
          Next {billingPeriod}: {nextPeriodSeats}
        </div>
      )}
    </div>
  );

  return (
    <div className="billing-page-card__plan-info">
      {isTeamPlan && !!planTab.tabDetails.extraPages && (
        <SubscriptionMetaField
          label="Additional Page Slots:"
          value={(planTab.tabDetails.extraPages as number).toString()}
        />
      )}

      {!isTeamPlan && (
        <SubscriptionMetaField
          label={page.realFacebookPage ? 'Facebook page ID:' : 'Account ID:'}
          value={
            page.realFacebookPage ? (
              <a
                className="billing-page-card__facebook-link"
                href={`https://facebook.com/${page.remoteId}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {page?.remoteId}
              </a>
            ) : (
              <span>{page?.remoteId}</span>
            )
          }
        />
      )}
      <SubscriptionMetaField
        label="Subscription Term:"
        value={subscriptionTerm}
      />
      <SubscriptionMetaField
        label="Subscription Status:"
        value={subscriptionStatus}
      />
      <SubscriptionMetaField label="Plan expires:" value={subscriptionEndsAt} />
      <SubscriptionMetaField
        label="Next Billing Date:"
        value={nextBillingDate}
      />
      {showFieldNextBillAmount && (
        <SubscriptionMetaField
          label="Next Bill Amount:"
          value={nextBillCombinedInfo}
        />
      )}
      <SubscriptionMetaField label="Billed With:" value={billedWith} />
      {showSendCreditsAllotmentsAndSeats && !isBilling2023Plan && (
        <SubscriptionMetaField
          label="Total Allotted Credits:"
          value={allotedCredits}
        />
      )}
      {showSendCreditsAllotmentsAndSeats &&
        !!(thisPeriodSeats || nextPeriodSeats) && (
          <SubscriptionMetaField
            label="Additional Seats:"
            value={additionalSeats}
          />
        )}
    </div>
  );
};
